import * as React from 'react'
import { Helmet } from 'react-helmet'
import { Message } from 'semantic-ui-react'
import { useParams, useHistory } from 'react-router-dom'
import useSWR from 'swr'
import { z } from 'zod'

import FunnelBoard from '../../components/funnels/FunnelBoard'
import FunnelFilters from '../../components/funnels/FunnelFilters'
import FunnelViewsHeader from '../../components/funnels/FunnelViewsHeader'
import PageLoader from '../../components/pageLoader'
import useFunnels from '../../hooks/useFunnels'
import { loadFunnelData, FunnelWithCards } from '../../hooks/useFunnel'
import usePowers from '../../hooks/usePowers'
import { useApi, useTalentApi } from '../../store/mainContext'
import { Collection, Funnel } from '../../types'
import { validateResponse } from '../../utils'

type SupportedCollection = Extract<Collection, 'candidates' | 'contacts'>

type Props = {
  collection: SupportedCollection
}

const COLUMN_WIDTH = 220

const funnelViewSchema = z.object({
  _id: z.string(),
  name: z.string(),
  title: z.string(),
  funnelsToShow: z.array(z.string()),
  relatedCollection: z.string(),
})

type FunnelViewConfig = z.infer<typeof funnelViewSchema>

type CombinedFunnelData = FunnelWithCards & {
  columnOrigins: Record<string, { title: string; name: string }>
}

function useCombinedFunnelData(
  collection: SupportedCollection,
  funnels: Funnel[] | undefined,
  viewName: string | undefined,
  viewTitle?: string,
) {
  const api = useApi()
  const powers = usePowers()

  const key =
    viewName && funnels && funnels.length > 0
      ? [
          'funnel-view-combined',
          collection,
          viewName,
          ...funnels.map((funnel) => funnel._id),
        ]
      : null

  return useSWR<CombinedFunnelData | undefined>(
    key,
    async () => {
      if (!viewName || !funnels || funnels.length === 0) return undefined

      const results = await Promise.all(
        funnels.map((funnel) => loadFunnelData(api, powers, funnel.name)),
      )

      const validResults = results.filter((result): result is FunnelWithCards =>
        Boolean(result),
      )

      if (validResults.length === 0) return undefined

      const first = validResults[0]!

      const columnOrigins: Record<string, { title: string; name: string }> = {}
      const combinedColumns = validResults.flatMap((result) =>
        result.funnel.columns.map((column) => {
          const newColumn = { ...column }
          columnOrigins[newColumn.id] = {
            title: result.funnel.title,
            name: result.funnel.name,
          }
          return newColumn
        }),
      )

      const combinedFunnel: Funnel = {
        ...first.funnel,
        _id: `${collection}-${viewName}-combined`,
        name: `${collection}-${viewName}-combined`,
        title: viewTitle || first.funnel.title,
        sub: `Combined view of ${validResults.length} funnel${
          validResults.length > 1 ? 's' : ''
        }.`,
        order: 0,
        columns: combinedColumns,
        collection,
        cardType: first.funnel.cardType,
      }

      const cards = validResults.flatMap((result) => result.cards)

      return { funnel: combinedFunnel, cards, columnOrigins }
    },
    {
      revalidateOnFocus: false,
    },
  )
}

function FunnelsPage({ collection }: Props) {
  const { viewName } = useParams<{ viewName: string }>()
  const history = useHistory()

  const talentApi = useTalentApi()

  const {
    data: viewConfig,
    isLoading: isLoadingView,
    error: viewError,
  } = useSWR<FunnelViewConfig | null>(
    viewName ? ['funnel-view-config', collection, viewName] : null,
    async () => {
      const response = await talentApi
        .get('funnel-views', { params: { name: viewName } })
        .then(validateResponse(z.array(funnelViewSchema)))
      return response[0] || null
    },
  )

  const relatedCollectionParam =
    collection.charAt(0).toUpperCase() + collection.slice(1)

  const { data: availableViews, isLoading: isLoadingAvailableViews } = useSWR<
    FunnelViewConfig[]
  >(['funnel-view-list', collection], () =>
    talentApi
      .get('funnel-views', {
        params: { relatedCollection: relatedCollectionParam },
      })
      .then(validateResponse(z.array(funnelViewSchema)))
      .then((views) => views.sort((a, b) => a.title.localeCompare(b.title))),
  )

  const {
    data: funnels,
    isLoading: isLoadingList,
    error: funnelsError,
  } = useFunnels(collection)

  const selectedFunnels = React.useMemo(() => {
    if (!viewConfig || !funnels) return undefined
    if (!Array.isArray(viewConfig.funnelsToShow)) return undefined

    const mapByName = new Map(funnels.map((funnel) => [funnel.name, funnel]))
    const picked: Funnel[] = []
    const seen = new Set<string>()

    viewConfig.funnelsToShow.forEach((name) => {
      if (seen.has(name)) return
      const funnel = mapByName.get(name)
      if (funnel) {
        picked.push(funnel)
        seen.add(name)
      }
    })

    return picked
  }, [funnels, viewConfig])

  const viewTitle =
    viewConfig?.title ||
    `${collection.charAt(0).toUpperCase()}${collection.slice(1)} Funnels`

  const {
    data: combinedData,
    isLoading: isLoadingCombined,
    error: combinedError,
  } = useCombinedFunnelData(collection, selectedFunnels, viewName, viewTitle)

  const scrollContainerRef = React.useRef<HTMLDivElement>(null)

  const handleHorizontalWheel = React.useCallback(
    (event: React.WheelEvent<HTMLDivElement>) => {
      const container = scrollContainerRef.current
      if (!container) return

      const { deltaX, deltaY } = event
      const isHorizontalGesture = Math.abs(deltaX) > Math.abs(deltaY)
      if (!isHorizontalGesture && !event.shiftKey) return

      event.preventDefault()
      const scrollDelta = deltaX !== 0 ? deltaX : deltaY
      container.scrollLeft += scrollDelta
    },
    [],
  )

  if (!viewName) {
    return (
      <Message warning>
        Missing funnel view identifier. Navigate to a valid{' '}
        <code>/funnels/views/:viewName</code> path.
      </Message>
    )
  }

  if (isLoadingView || isLoadingList || isLoadingCombined) {
    return <PageLoader />
  }

  if (viewError) {
    return (
      <Message error>
        Unable to load funnel view <code>{viewName}</code>. {viewError.message}
      </Message>
    )
  }

  if (!viewConfig) {
    return (
      <Message warning>
        Funnel view <code>{viewName}</code> was not found.
      </Message>
    )
  }

  const relatedCollection = viewConfig.relatedCollection?.toLowerCase() as
    | SupportedCollection
    | undefined

  if (relatedCollection && relatedCollection !== collection) {
    return (
      <Message error>
        Funnel view <code>{viewName}</code> belongs to{' '}
        <code>{viewConfig.relatedCollection}</code>, not{' '}
        <code>{collection}</code>.
      </Message>
    )
  }

  if (funnelsError) {
    return (
      <Message error>
        Unable to load funnels for <code>{collection}</code>.
      </Message>
    )
  }

  if (combinedError) {
    return (
      <Message error>
        Unable to combine funnels for view <code>{viewName}</code>.
      </Message>
    )
  }

  if (!selectedFunnels || selectedFunnels.length === 0) {
    return (
      <Message warning>
        No funnels are configured for view <code>{viewName}</code>.
      </Message>
    )
  }

  if (!combinedData) {
    return (
      <Message warning>
        No data available for the selected funnels in view{' '}
        <code>{viewName}</code>.
      </Message>
    )
  }

  const collectionLabel =
    collection.charAt(0).toUpperCase() + collection.slice(1)
  const funnelsCount = selectedFunnels.length
  const headerDescription = `Showing ${funnelsCount} funnel${
    funnelsCount === 1 ? '' : 's'
  } for ${collectionLabel}.`

  return (
    <div className="relative flex h-screen flex-col bg-slate-50">
      <Helmet>
        <title>{viewTitle} - Rome</title>
      </Helmet>

      <FunnelFilters funnel={combinedData.funnel} cards={combinedData.cards}>
        {({ filteredCards, filtersElement }) => (
          <>
            <FunnelViewsHeader
              views={availableViews || []}
              currentViewName={viewName}
              isLoading={isLoadingAvailableViews}
              onViewChange={(newViewName) =>
                history.push(`/${collection}/funnels/views/${newViewName}`)
              }
              filtersElement={filtersElement}
              description={headerDescription}
            />

            <div className="flex grow flex-col overflow-hidden">
              <div
                className="relative grow overflow-x-auto"
                ref={scrollContainerRef}
                onWheel={handleHorizontalWheel}
                style={{ overscrollBehaviorX: 'contain' }}
              >
                <div className="min-h-full min-w-full">
                  <FunnelBoard
                    funnel={combinedData.funnel}
                    cards={filteredCards}
                    columnWidth={COLUMN_WIDTH}
                    columnOrigins={combinedData.columnOrigins}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </FunnelFilters>
    </div>
  )
}

export const CandidatesFunnelsPage = () => (
  <FunnelsPage collection="candidates" />
)

export const ContactsFunnelsPage = () => <FunnelsPage collection="contacts" />

export default FunnelsPage
